import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { useObserver } from 'mobx-react-lite';
import { ErrorBoundary } from 'react-error-boundary';
import { createRemoteAppComponent } from '@module-federation/bridge-react/base';
import { loadRemote } from '@module-federation/runtime';

import { SmallLoader } from 'shared/components/loading';
import MetaTags from 'src/components/meta-tags';
import { useIsMounted } from 'hooks/use-is-mounted';
import useIframeHeightOverride from 'hooks/use-iframe-height-override';
import useViewport from 'hooks/use-viewport';
import useUI from 'src/hooks/use-ui';
import { getLogger } from 'src/utils/datadog-logger';

import { useConciergeBridge } from './bridge/use-concierge-bridge';
import { ConciergeProviders } from './concierge-providers';
import { useRegisterConciergeHostPresence } from './idle/concierge-host-presence';
import {
  CONCIERGE_REMOTE_NAME,
  reinitializeConciergeRemote,
  useConciergeRemoteRegistration,
} from './use-concierge-remote-registration';
import { useConciergeTheme } from './use-concierge-theme';
import { conciergeScopeProps } from './use-concierge-theme-scope';
import { useKioskHeaderOffset } from './use-kiosk-header-offset';

const logger = getLogger('concierge-host');

export const CONCIERGE_LOAD_TIMEOUT_MS = 4_000;
export const CONCIERGE_MAX_LOAD_ATTEMPTS = 3;
export const CONCIERGE_RETRY_DELAY_MS = 200;

export async function loadConciergeRemoteWithRetry(
  maxAttempts = CONCIERGE_MAX_LOAD_ATTEMPTS,
  timeoutMs = CONCIERGE_LOAD_TIMEOUT_MS,
  initialDelayMs = CONCIERGE_RETRY_DELAY_MS
): Promise<unknown> {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      if (attempt > 1) {
        reinitializeConciergeRemote();
      }

      const loadPromise = loadRemote(`${CONCIERGE_REMOTE_NAME}/ConciergeApp`);
      // Attach a no-op rejection handler to swallow late rejections if the timeout
      // wins the race and the abandoned load settles later with an error.
      loadPromise.catch(() => undefined);
      let timer: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          reject(new Error(`Concierge MF: load timed out after ${timeoutMs}ms (attempt ${attempt}/${maxAttempts})`));
        }, timeoutMs);
      });

      const result = await Promise.race([loadPromise, timeoutPromise]).finally(() => {
        if (timer) {
          clearTimeout(timer);
        }
      });

      if (!result || typeof result !== 'object') {
        throw new Error(`Concierge MF: failed to load ${CONCIERGE_REMOTE_NAME}/ConciergeApp`);
      }

      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      lastError = error;

      if (attempt < maxAttempts) {
        const delay = initialDelayMs * 2 ** (attempt - 1);
        logger.warn('Concierge remote load failed, retrying...', {
          attempt,
          maxAttempts,
          delayMs: delay,
          error: error.message,
        });
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  logger.error('Concierge remote load exhausted all retries', {
    maxAttempts,
    error: lastError?.message,
  });
  throw (
    lastError ??
    new Error(`Concierge MF: failed to load ${CONCIERGE_REMOTE_NAME}/ConciergeApp after ${maxAttempts} attempts`)
  );
}

function ConciergeLoading(): JSX.Element {
  return (
    <Centered aria-label='Loading concierge'>
      <SmallLoader height={80} />
    </Centered>
  );
}

export function ConciergeUnavailable({ error, onRetry }: { error?: Error; onRetry?: () => void }): JSX.Element {
  useEffect(() => {
    if (error) {
      logger.error('Concierge unavailable', {
        message: error.message,
        stack: error.stack,
        remote: CONCIERGE_REMOTE_NAME,
      });
    }
  }, [error]);

  return (
    <Centered role='alert'>
      <UnavailableTitle>Concierge is temporarily unavailable.</UnavailableTitle>
      <UnavailableSubtitle>Please try again later.</UnavailableSubtitle>
      {onRetry && (
        <RetryButton type='button' onClick={onRetry}>
          Try again
        </RetryButton>
      )}
    </Centered>
  );
}

// Shell: theme and layout only. Everything that can fail renders inside ConciergeBoundary, so its
// fallback stays inside the themed providers and Page.
export function ConciergeHost(): JSX.Element {
  useRegisterConciergeHostPresence();
  const { height: viewportHeight } = useViewport();
  useIframeHeightOverride(viewportHeight);

  const resolvedCustomSettings = useConciergeTheme();

  const UI = useUI();
  const isKiosk = useObserver(() => UI.isKiosk);
  const headerOffset = useKioskHeaderOffset(isKiosk);

  const rejectionKey = resolvedCustomSettings.rejections.join('|');
  const { source } = resolvedCustomSettings;
  useEffect(() => {
    if (!rejectionKey) {
      return;
    }
    logger.warn('Ignored invalid concierge theme tokens', { rejections: rejectionKey, source });
    // Partner-facing signal for someone iterating with ?manifest_url=.
    console.warn(`[concierge] Ignored invalid theme tokens:\n  ${rejectionKey.split('|').join('\n  ')}`);
  }, [rejectionKey, source]);

  return (
    <ConciergeProviders customization={resolvedCustomSettings.settings} isKiosk={isKiosk}>
      <MetaTags customRobotsRule='noindex,nofollow' />
      <Page
        // On kiosk <body> keeps the store colors, so the mount carries the Concierge scheme.
        {...(isKiosk ? conciergeScopeProps(resolvedCustomSettings.settings) : {})}
        data-testid='concierge-mf-mount'
        $isKiosk={isKiosk}
        style={
          isKiosk ? ({ '--kiosk-concierge-header-offset': `${headerOffset}px` } as React.CSSProperties) : undefined
        }
      >
        <ConciergeBoundary />
      </Page>
    </ConciergeProviders>
  );
}

// Boundary: owns failure handling. One ErrorBoundary and one retry counter; every fallback retries
// through it, and the key remounts ConciergeContent so its hooks and the remote are rebuilt.
function ConciergeBoundary(): JSX.Element {
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => {
    setAttempt((prev) => prev + 1);
  }, []);

  return (
    <ErrorBoundary key={attempt} fallbackRender={({ error }) => <ConciergeUnavailable error={error} onRetry={retry} />}>
      <ConciergeContent attempt={attempt} onRetry={retry} />
    </ErrorBoundary>
  );
}

function useConciergeRemote(attempt: number, onRetry: () => void): ReturnType<typeof createRemoteAppComponent> {
  return useMemo(() => {
    void attempt;
    return createRemoteAppComponent({
      loader: () => loadConciergeRemoteWithRetry(),
      loading: <ConciergeLoading />,
      fallback: (props: { error?: Error }) => <ConciergeUnavailable {...props} onRetry={onRetry} />,
    });
  }, [onRetry, attempt]);
}

// Content: everything that can fail -- the bridge hooks, remote registration, and the remote.
function ConciergeContent({ attempt, onRetry }: { attempt: number; onRetry: () => void }): JSX.Element {
  // The concierge remote owns its session state — nothing session-related is
  // plumbed through the bridge.
  const bridgeProps = useConciergeBridge();
  const status = useConciergeRemoteRegistration(attempt);
  const ConciergeRemote = useConciergeRemote(attempt, onRetry);

  // The concierge is a Module Federation remote — a client-only bundle with no
  // server-renderable form (MF's runtime is never init'd in Node). This route
  // server-renders (ECOMM-11724), so mounting the remote directly would (a)
  // hydration-mismatch, since the server can't produce the same tree, and (b)
  // invoke loadRemote during SSR against an uninitialized runtime. Gate it on
  // mount: server and first client render both emit <ConciergeLoading/>, then
  // the real remote loads client-side after hydration.
  const isMounted = useIsMounted();

  if (!isMounted) {
    return <ConciergeLoading />;
  }

  if (status === 'unavailable') {
    return <ConciergeUnavailable onRetry={onRetry} />;
  }

  return <ConciergeRemote props={bridgeProps} />;
}

// position: relative anchors the absolute-positioned Centered fallback.
const Page = styled.div<{ $isKiosk: boolean }>`
  position: relative;
  height: 100vh;
  height: 100dvh;
  background: var(--dcm-color-body-bg);
  color: var(--dcm-color-text-primary);

  ${({ $isKiosk }) =>
    $isKiosk &&
    css`
      /* Kiosk chromes deliver a bounded box: Kiosk Pro exports its fixed
         top-chrome and footer clearance as CSS variables, and the regular
         kiosk's sticky header height is measured into the inline offset. Each
         variable falls back to 0px so a chrome only subtracts its own. dvh keeps
         the box above the tablet keyboard, so the composer squeezes instead of
         hiding. */
      height: calc(
        100dvh - var(--kiosk-pro-top-offset, 0px) - var(--kiosk-pro-footer-height, 0px) -
          var(--kiosk-concierge-header-offset, 0px)
      );
      /* Floor for a short box (small landscape tablet, tall chrome). The remote
         scrolls only its message list (chat-column overflow-y-auto) and keeps the
         composer as a non-shrinking sibling, so a box shorter than composer plus
         padding would clip the composer. 400px = ~48px chat padding + ~120px
         composer + ~230px of messages. The document scrolls past the floor. */
      min-height: 400px;
      overflow: hidden;

      /* Module Federation's bridge renders its own auto-height wrapper div and
         the remote mounts its root inside it, so the remote's own full-height
         rule has nothing to resolve against. Give the whole chain a height. */
      > div {
        height: 100%;
        min-height: 0;
      }

      /* Not the unavailable fallback (Centered): its text and button children
         must keep their natural height. */
      > div:not(${Centered}) > * {
        height: 100%;
        min-height: 0;
      }
    `}
`;

const Centered = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--dcm-space-6);
  gap: var(--dcm-space-2);
`;

const UnavailableTitle = styled.h2`
  margin: 0;
  font-size: var(--dcm-font-size-lg);
  color: var(--dcm-color-text-primary);
`;

const UnavailableSubtitle = styled.p`
  margin: 0;
  color: var(--dcm-color-text-secondary);
`;

const RetryButton = styled.button`
  margin-top: var(--dcm-space-2, 8px);
  padding: var(--dcm-space-2, 8px) var(--dcm-space-4, 16px);
  font-size: var(--dcm-font-size-sm, 14px);
  font-weight: 600;
  color: var(--dcm-color-button-primary-text, #ffffff);
  background-color: var(--dcm-color-button-primary-bg, #121516);
  border: 1px solid transparent;
  border-radius: var(--dcm-border-radius-md, 4px);
  cursor: pointer;
  transition: opacity 0.15s ease-in-out;

  &:hover {
    opacity: 0.85;
  }
`;
